import { i18n } from '../../../../translate/i18n'
import { FC, useState } from 'react'
import { ToursTabProps } from '../types/tabTypes'
import { BookingContent } from '../../../../../Types/HotelBookingTypes'

const ToursTab: FC<ToursTabProps> = ({ tours }) => {
  const [expandedTours, setExpandedTours] = useState<Set<number>>(new Set())

  const toggleTour = (tourId: number) => {
    setExpandedTours(prev => {
      const newSet = new Set(prev)
      if (newSet.has(tourId)) {
        newSet.delete(tourId)
      } else {
        newSet.add(tourId)
      }
      return newSet
    })
  }
  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_confirmed')
      case 'pending':
        return i18n.t('hospitality_room_reservation_tabs.services.status_pending')
      case 'cancelled':
        return i18n.t('hospitality_room_reservation_tabs.services.status_cancelled')
      case 'completed':
        return i18n.t('hospitality_room_reservation_tabs.services.status_completed')
      default:
        return status
    }
  }

  if (tours.length === 0) {
    return (
      <div className='text-center py-10'>
        <i className='fas fa-map-marked-alt text-muted fs-3x mb-4'></i>
        <h3 className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.tours.label_no_data')}</h3>
        <p className='text-muted'>{i18n.t('hospitality_room_reservation_tabs.tours.label_pending')}</p>
      </div>
    )
  }

  return (
    <div className='card card-xxl-stretch'>
      <div className='card-header py-5'>
        <h3 className='card-title'>
          <i className='fas fa-map-marked-alt me-2'></i>
          {i18n.t('hospitality_room_reservation_tabs.tours.tab_title')}
        </h3>
        <div className='card-toolbar'>
          <span className='badge badge-light-info'>
            {tours.length} {tours.length === 1 ? i18n.t('hospitality_room_reservation_tabs.tours.label_tour') : i18n.t('hospitality_room_reservation_tabs.tours.label_tours')}
          </span>
        </div>
      </div>
      <div className='card-body'>
        <div className='row'>
          {tours.map((tour) => {
            const content: BookingContent = JSON.parse(tour.content || '{}')
            const isExpanded = expandedTours.has(tour.id)
            return (
              <div key={tour.id} className='col-12 mb-4'>
                <div className='card card-flush border border-gray-300'>
                  <div 
                    className='card-header bg-light-info' 
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleTour(tour.id)}
                  >
                    <div className='d-flex justify-content-between align-items-center w-100'>
                      <div className='d-flex align-items-center'>
                        <i className='fas fa-map-marked-alt text-info fs-2x me-3'></i>
                        <div>
                          <h4 className='card-title text-info mb-1'>{tour.hotelTour.title}</h4>
                          <div className='d-flex align-items-center flex-wrap gap-3'>
                            <span className='text-muted'>
                              <i className='fas fa-calendar me-1'></i>
                              {new Date(tour.reservationDate).toLocaleDateString('pt-BR')}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-users me-1'></i>
                              {tour.participants} {i18n.t('hospitality_room_reservation_tabs.tours.label_people')}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-clock me-1'></i>
                              {tour.hotelTour.duration}
                            </span>
                            <span className='text-muted'>
                              <i className='fas fa-star me-1'></i>
                              {tour.hotelTour.rating} ⭐
                            </span>
                            <span className={`badge badge-${tour.hotelTour.includesTransfer ? 'success' : 'secondary'}`}>
                              <i className='fas fa-shuttle-van me-1'></i>
                              {tour.hotelTour.includesTransfer ? i18n.t('hospitality_room_reservation_tabs.tours.label_transfer_yes') : i18n.t('hospitality_room_reservation_tabs.tours.label_transfer_no')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex align-items-center'>
                        <div className='text-end me-3'>
                          <h4 className='text-info mb-0'>{tour.currency} {parseFloat(tour.totalAmount).toFixed(2)}</h4>
                        </div>
                        <span className={`badge badge-${tour.status === 'confirmed' ? 'success' : tour.status === 'cancelled' ? 'danger' : 'warning'} me-3`}>
                          {getStatusLabel(tour.status)}
                        </span>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-muted`}></i>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                  <div className='card-body'>
                    <div className='row mb-4'>
                      <div className='col-12'>
                        <h5 className='text-gray-700 fw-bold mb-3'>
                          <i className='fas fa-info-circle text-primary me-2'></i>
                        </h5>
                        <div 
                          className='text-gray-600'
                          dangerouslySetInnerHTML={{ __html: tour.hotelTour.description }}
                        />
                      </div>
                    </div>

                    <div className='row'>
                      <div className='col-md-3 mb-3'>
                        <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.tours.label_difficulty')}</small>
                        <div className='fw-bold text-capitalize'>{tour.hotelTour.difficulty}</div>
                      </div>
                      {content.selectedSchedule && (
                        <div className='col-md-3 mb-3'>
                          <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.tours.label_schedule')}</small>
                          <div className='fw-bold'>{content.selectedSchedule}</div>
                        </div>
                      )}
                    </div>

                    <div className='mb-4 p-3 bg-light rounded'>
                      <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.tours.label_physical_requirements')}</small>
                      <div className='fw-bold'>{tour.hotelTour.physicalRequirements}</div>
                    </div>

                    {content.weatherConcerns && (
                      <div className='alert alert-warning d-flex align-items-center mb-3'>
                        <i className='fas fa-cloud-rain me-2'></i>
                        <small>{i18n.t('hospitality_room_reservation_tabs.tours.label_weather_concerns')}</small>
                      </div>
                    )}

                    {tour.specialRequests && (
                      <div className='p-3 bg-light-info rounded'>
                        <small className='text-muted d-block'>{i18n.t('hospitality_room_reservation_tabs.tours.label_special_requests')}</small>
                        <div className='fw-bold text-info'>{tour.specialRequests}</div>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ToursTab